/**
 * HotelRequestsInboxPage - Inbox centralizzata segnalazioni/acquisti/fornitori extra
 * dagli hotel. RBAC: super_admin (tutto), hotel_manager e capo_manutentore (assigned_hotels).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2, RefreshCw, Plus, AlertTriangle, Building2, Wrench, ShoppingCart, MessageSquare, Paperclip,
} from 'lucide-react';
import NewHotelRequestDialog from '../components/hotel-requests/NewHotelRequestDialog';

// Palette coordinata con il resto dell'app (Tickets/Contract): bg-*-50 text-*-700 border-*-200,
// più varianti dark:*/10 text-*/300 border-*/30 per il tema scuro.
const TYPE_META = {
  riparazione: {
    label: 'Riparazione',
    icon: Wrench,
    badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
    iconWrap: 'bg-amber-50 dark:bg-amber-500/10',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  acquisto: {
    label: 'Acquisto',
    icon: ShoppingCart,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30',
    iconWrap: 'bg-emerald-50 dark:bg-emerald-500/10',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  fornitore_extra: {
    label: 'Fornitore',
    icon: Building2,
    badge: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/30',
    iconWrap: 'bg-violet-50 dark:bg-violet-500/10',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
};

const STATUS_META = {
  ricevuta:            { label: 'Ricevuta',            badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/30',       dot: 'bg-rose-500' },
  presa_in_carico:     { label: 'Presa in carico',     badge: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30',            dot: 'bg-sky-500' },
  fornitore_assegnato: { label: 'Fornitore assegnato', badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30', dot: 'bg-amber-500' },
  in_lavorazione:      { label: 'In lavorazione',      badge: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30', dot: 'bg-amber-500' },
  completata:          { label: 'Completata',          badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30', dot: 'bg-emerald-500' },
  archiviata:          { label: 'Archiviata',          badge: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',      dot: 'bg-slate-400' },
};

// La barra sinistra della card riflette la PRIORITÀ (il segnale più urgente per l'occhio).
const PRIORITY_META = {
  urgente: { label: 'URGENTE', text: 'text-red-600 dark:text-red-400',       bar: 'border-l-red-500' },
  alta:    { label: 'ALTA',    text: 'text-orange-600 dark:text-orange-400', bar: 'border-l-orange-500' },
  media:   { label: 'MEDIA',   text: 'text-sky-600 dark:text-sky-400',       bar: 'border-l-sky-400' },
  bassa:   { label: 'BASSA',   text: 'text-slate-500 dark:text-slate-400',   bar: 'border-l-slate-300 dark:border-l-slate-600' },
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'ora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} gg fa`;
  } catch { return ''; }
};

const HotelRequestsInboxPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHotel, setFilterHotel] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [openNew, setOpenNew] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterHotel !== 'all') params.append('hotel_id', filterHotel);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      if (search.trim()) params.append('search', search.trim());
      const [listRes, statsRes] = await Promise.all([
        api.get(`/hotel-requests?${params.toString()}`),
        api.get('/hotel-requests/stats'),
      ]);
      setItems(listRes.data.items || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento richieste');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterHotel, filterPriority, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/hotels').then(r => setHotels(r.data || [])).catch(() => {});
  }, []);

  const counters = useMemo(() => {
    const c = { all: items.length, riparazione: 0, acquisto: 0, fornitore_extra: 0 };
    items.forEach(i => { c[i.type] = (c[i.type] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="space-y-6 max-w-7xl" data-testid="hotel-requests-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Inbox Richieste</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Tutte le segnalazioni dagli hotel in un unico posto. Basta WhatsApp, basta mail sparse.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-requests">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button onClick={() => setOpenNew(true)} className="bg-slate-900 hover:bg-slate-800 text-white gap-1" data-testid="new-request-btn">
            <Plus className="w-4 h-4" /> Nuova Richiesta
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-red-200 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/10">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-red-700 dark:text-red-300 font-semibold">Aspettano te</p>
            <p className="text-3xl font-bold text-red-600 dark:text-red-300 mt-1" data-testid="kpi-waiting-me">{stats?.waiting_me ?? '—'}</p>
            <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-1">{stats?.waiting_me_urgent_stuck || 0} urgenti · oltre 24h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">In attesa fornitore</p>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mt-1" data-testid="kpi-waiting-supplier">{stats?.waiting_supplier ?? '—'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
              {(stats?.top_suppliers || []).slice(0, 3).map(s => `${s.count} ${s.name}`).join(' · ') || 'nessun fornitore'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Chiuse settimana</p>
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1" data-testid="kpi-closed-week">{stats?.closed_this_week ?? '—'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Ultimi 7 giorni</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Totale aperte</p>
            <p className="text-3xl font-bold text-slate-800 dark:text-slate-100 mt-1" data-testid="kpi-total-open">{stats?.total_open ?? '—'}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stats?.hotels_involved || 0} hotel coinvolti</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            {[
              { key: 'all', label: `Tutte (${counters.all})` },
              { key: 'riparazione', label: `🛠️ Riparazioni (${counters.riparazione || 0})` },
              { key: 'acquisto', label: `🛒 Acquisti (${counters.acquisto || 0})` },
              { key: 'fornitore_extra', label: `🏢 Fornitori (${counters.fornitore_extra || 0})` },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setFilterType(t.key)}
                data-testid={`filter-type-${t.key}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${filterType === t.key ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-white' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300'}`}
              >{t.label}</button>
            ))}
          </div>
          <Select value={filterHotel} onValueChange={setFilterHotel}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-hotel"><SelectValue placeholder="Hotel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="ricevuta">Ricevuta</SelectItem>
              <SelectItem value="presa_in_carico">Presa in carico</SelectItem>
              <SelectItem value="fornitore_assegnato">Fornitore assegnato</SelectItem>
              <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
              <SelectItem value="completata">Completata</SelectItem>
              <SelectItem value="archiviata">Archiviata</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="filter-priority"><SelectValue placeholder="Priorità" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="urgente">Urgente</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
              <SelectItem value="media">Media</SelectItem>
              <SelectItem value="bassa">Bassa</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Cerca titolo o fornitore..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[180px] h-8 text-xs"
            data-testid="filter-search"
          />
        </CardContent>
      </Card>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500 dark:text-slate-400">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          Nessuna richiesta corrispondente ai filtri.
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="requests-list">
          {items.map((r) => {
            const typeMeta = TYPE_META[r.type] || TYPE_META.riparazione;
            const statusMeta = STATUS_META[r.status] || STATUS_META.ricevuta;
            const prio = PRIORITY_META[r.priority] || PRIORITY_META.media;
            const TypeIcon = typeMeta.icon;
            const firstPhoto = (r.photos || [])[0];
            const photoCount = (r.photos || []).length;
            return (
              <Card
                key={r.id}
                onClick={() => navigate(`/richieste/${r.id}`)}
                className={`cursor-pointer hover:shadow-md transition border-l-4 ${prio.bar} ${r.status === 'completata' || r.status === 'archiviata' ? 'opacity-70' : ''}`}
                data-testid={`request-card-${r.id}`}
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className={`w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden relative flex items-center justify-center ${firstPhoto ? 'bg-slate-100 dark:bg-slate-800' : typeMeta.iconWrap}`}>
                    {firstPhoto ? (
                      <>
                        <img src={firstPhoto} alt="" className="w-full h-full object-cover" />
                        {photoCount > 1 && (
                          <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1.5 rounded-tl">+{photoCount - 1}</span>
                        )}
                      </>
                    ) : (
                      <TypeIcon className={`w-8 h-8 ${typeMeta.iconColor}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      <Badge variant="outline" className={`text-[10px] font-semibold uppercase tracking-wide ${typeMeta.badge}`}>{typeMeta.label}</Badge>
                      <Badge variant="outline" className={`text-[10px] font-medium inline-flex items-center gap-1 ${statusMeta.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                        {statusMeta.label}
                      </Badge>
                      <span className={`text-[10px] font-semibold tracking-wide ${prio.text}`}>{prio.label}</span>
                      {r.supplier_name && (
                        <Badge variant="outline" className="text-[10px] font-normal text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700">→ {r.supplier_name}</Badge>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{r.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {r.hotel_name} · {r.created_by_name} · {fmtRelative(r.created_at)}
                    </p>
                    {r.type === 'acquisto' && r.total > 0 && (
                      <p className="text-xs mt-1"><span className="font-semibold text-slate-800 dark:text-slate-100">€ {r.total.toFixed(2)}</span>{r.product_link && <a href={r.product_link} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="text-sky-600 dark:text-sky-400 ml-2 underline">🔗 link prodotto</a>}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0 text-xs text-slate-400 dark:text-slate-500">
                    {(r.comments?.length || 0) > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {r.comments.length}</span>}
                    {photoCount > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {photoCount} foto</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <NewHotelRequestDialog
        open={openNew}
        onOpenChange={setOpenNew}
        hotels={hotels}
        onCreated={() => { setOpenNew(false); load(); }}
      />
    </div>
  );
};

export default HotelRequestsInboxPage;
